
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { debugLogger } from '@/utils/debugLogger';

const VerifyTokenPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const confirmed = searchParams.get('confirmed');
  const error_code = searchParams.get('error_code');
  const error_description = searchParams.get('error_description');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      setLoading(true);

      try {
        // Verificar se houve erro na URL
        if (error_code || error_description) {
          setError(error_description || 'Erro na verificação do email');
          setLoading(false);
          return;
        }

        // Verificar se o email foi confirmado
        if (confirmed === 'true') {
          setSuccess(true);
          toast({
            title: "Email Verificado!",
            description: "Seu email foi verificado com sucesso. Você pode fazer login agora.",
          });
          
          // Redirecionar após 3 segundos
          setTimeout(() => {
            navigate('/auth');
          }, 3000);
        } else {
          // Verificar o status atual do usuário
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user?.email_confirmed_at) {
            setSuccess(true);
            toast({
              title: "Email já verificado!",
              description: "Seu email já foi verificado anteriormente.",
            });
            
            setTimeout(() => {
              navigate('/main/feed');
            }, 2000);
          } else {
            setError('Email ainda não foi verificado');
          }
        }
      } catch (err: any) {
        debugLogger.error('Error verifying email:', err);
        setError('Erro ao verificar status do email');
      } finally {
        setLoading(false);
      }
    };

    handleEmailConfirmation();
  }, [confirmed, error_code, error_description, navigate]);

  const handleResendVerification = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user?.email) {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email
      });
      
      if (error) {
        toast({
          title: "Erro",
          description: "Não foi possível reenviar o email de verificação.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Email Reenviado",
          description: "Verifique sua caixa de entrada.",
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-black/40 border-white/10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-luxury-red to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">LR</span>
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Verificação de Email
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center">
          {loading && (
            <div className="space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-luxury-red mx-auto" />
              <p className="text-white/70">Verificando seu email...</p>
            </div>
          )}

          {success && !loading && (
            <div className="space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h3 className="text-xl font-bold text-white">Email Verificado!</h3>
              <p className="text-white/70">
                Seu email foi verificado com sucesso. Redirecionando...
              </p>
              <div className="flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-luxury-red mr-2" />
                <span className="text-white/60">Redirecionando...</span>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="space-y-4">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h3 className="text-xl font-bold text-white">Erro na Verificação</h3>
              <p className="text-white/70">{error}</p>
              
              <div className="space-y-2">
                <Button
                  onClick={handleResendVerification}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Reenviar Email
                </Button>
                
                <Button
                  onClick={() => navigate('/auth')}
                  className="w-full bg-luxury-red hover:bg-luxury-red/90"
                >
                  Voltar ao Login
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyTokenPage;
